import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { API, apiError, downloadCsv } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Code2, Copy, RefreshCw, Smartphone, Webhook, KeyRound, Loader2, Terminal, ArrowRight,
  Download, FlaskConical, CheckCircle2, XCircle,
} from "lucide-react";
import { toast } from "sonner";

const Copyable = ({ value, testid, mono = true }) => (
  <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2">
    <span className={`flex-1 truncate text-sm ${mono ? "font-mono" : ""}`} data-testid={testid}>{value}</span>
    <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0"
      onClick={() => { navigator.clipboard.writeText(value); toast.success("Copied"); }}>
      <Copy className="h-3.5 w-3.5" />
    </Button>
  </div>
);

const CodeBlock = ({ children, testid }) => (
  <pre className="rounded-lg border bg-[hsl(var(--muted))] p-4 overflow-x-auto text-xs font-mono leading-relaxed" data-testid={testid}>
    <code>{children}</code>
  </pre>
);

export default function Developer() {
  const { user } = useAuth();
  const [apiKey, setApiKey] = useState(user.api_key || "");
  const [regening, setRegening] = useState(false);
  const [smsText, setSmsText] = useState("Rs 499.37 credited to A/c ...1234 on 03-10 UPI Ref 144293907719");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [presets, setPresets] = useState([]);

  useEffect(() => {
    api.get("/webhook/sms/presets").then((r) => setPresets(r.data.presets || [])).catch(() => {});
  }, []);

  const webhookUrl = `${API}/webhook/sms`;
  const collectUrl = `${API}/public/collect`;

  const regenerate = async () => {
    setRegening(true);
    try {
      const { data } = await api.post("/merchant/regenerate-key", {});
      setApiKey(data.api_key);
      setTestResult(null);
      toast.success("API key regenerated");
    } catch {
      toast.error("Failed to regenerate key");
    } finally {
      setRegening(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/webhook/sms", { api_key: apiKey, message: smsText });
      setTestResult({ ok: data.matched, data });
      toast[data.matched ? "success" : "message"](
        data.matched ? `Matched order ${data.order_id}` : `Parsed ₹${data.amount} — no pending order matched`
      );
    } catch (e) {
      setTestResult({ ok: false, error: apiError(e.response?.data?.detail) });
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  const downloadConfig = () => {
    const config = {
      service: "WoltPe SMS Forwarder",
      description: "Import into an Android 'SMS to URL Forwarder' app to auto-reconcile bank credit SMS.",
      webhook_url: webhookUrl,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body_template: { api_key: apiKey, message: "%sms_body%" },
      note: "Replace %sms_body% with your forwarder app's SMS-content placeholder if it differs (e.g. {{message}} or %text%). Add a sender/keyword filter so only bank credit alerts are forwarded.",
    };
    const blob = new Blob([JSON.stringify(config, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "woltpe-sms-forwarder-config.json";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success("Config downloaded");
  };

  const collectCurl = `curl -X POST "${collectUrl}" \\
  -H "x-api-key: ${apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{"amount": 499, "customer_name": "Rahul", "note": "Order #1234"}'`;

  const collectResp = `{
  "order_id": "WP2610ABCD1234",
  "amount": 499.37,          // unique-paise amount to charge
  "vpa": "business@paytm",   // auto-selected via rotation
  "upi_string": "upi://pay?pa=...&am=499.37&cu=INR",
  "pay_url": "/pay/WP2610ABCD1234"
}`;

  const smsCurl = `curl -X POST "${webhookUrl}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "api_key": "${apiKey}",
    "message": "Rs 499.37 credited to A/c ...1234 UPI Ref 144293907719"
  }'`;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-bold tracking-tight">Developer & Integrations</h2>
        <p className="text-sm text-muted-foreground">Create collections from your own checkout and auto-reconcile via bank SMS.</p>
      </div>

      <Card className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <KeyRound className="h-4 w-4 text-primary" />
          <h3 className="font-heading font-semibold">Your API key</h3>
          <Badge variant="outline" className="ml-auto border-warning/30 bg-warning/10 text-warning">Keep secret</Badge>
        </div>
        <Copyable value={apiKey} testid="dev-api-key" />
        <Button variant="outline" size="sm" className="mt-3" onClick={regenerate} disabled={regening} data-testid="dev-regen-key">
          {regening ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="mr-1.5 h-4 w-4" /> Regenerate key</>}
        </Button>
        <p className="mt-2 text-xs text-muted-foreground">Regenerating immediately invalidates the old key in any integrations.</p>
      </Card>

      <Tabs defaultValue="api">
        <TabsList data-testid="dev-tabs">
          <TabsTrigger value="api" data-testid="dev-tab-api"><Code2 className="h-4 w-4 mr-1.5" /> Create Collections API</TabsTrigger>
          <TabsTrigger value="sms" data-testid="dev-tab-sms"><Smartphone className="h-4 w-4 mr-1.5" /> SMS Forwarder</TabsTrigger>
        </TabsList>

        <TabsContent value="api" className="space-y-4 mt-4">
          <Card className="p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Terminal className="h-4 w-4 text-primary" />
              <h3 className="font-heading font-semibold">Create a collection</h3>
              <Badge variant="outline" className="ml-2 font-mono text-[10px]">POST</Badge>
            </div>
            <Copyable value={collectUrl} testid="dev-collect-url" />
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">Request</p>
              <CodeBlock testid="dev-collect-curl">{collectCurl}</CodeBlock>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">Response</p>
              <CodeBlock>{collectResp}</CodeBlock>
            </div>
            <div className="rounded-lg border border-dashed p-3 text-sm text-muted-foreground flex items-start gap-2">
              <ArrowRight className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
              Redirect your customer to <span className="font-mono text-foreground">{`${window.location.origin}`}<span className="text-primary">{"{pay_url}"}</span></span> to show the QR + UPI intent page, or render the <span className="font-mono text-foreground">upi_string</span> as your own QR.
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="sms" className="space-y-4 mt-4">
          <Card className="p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Webhook className="h-4 w-4 text-primary" />
              <h3 className="font-heading font-semibold">Bank SMS → Webhook auto-reconciliation</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              Install any Android "SMS Forwarder" app on a phone that receives credit alerts for your UPI bank
              account, import the config below, and WoltPe will auto-mark matching orders as
              <span className="text-success font-medium"> Success</span> — no demo button needed.
            </p>

            <div>
              <Label className="text-xs">Webhook URL</Label>
              <div className="mt-1.5"><Copyable value={webhookUrl} testid="dev-webhook-url" /></div>
            </div>

            <Button variant="secondary" onClick={downloadConfig} data-testid="dev-download-config">
              <Download className="mr-1.5 h-4 w-4" /> Download forwarder config (.json)
            </Button>

            <div className="space-y-3 pt-2">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Setup steps</p>
              {[
                ["Install an SMS forwarder", "Use “SMS Forwarder” or “SMS to URL Forwarder” from the Play Store."],
                ["Import the config", "Load the downloaded JSON (URL, POST, headers, body) — your api_key is already filled in."],
                ["Add a filter", "Forward only SMS from your bank sender (e.g. SBIUPI, HDFCBK) containing “credited”."],
                ["Done", "Paid orders auto-settle and appear instantly in the dashboard Live Feed."],
              ].map(([t, d], i) => (
                <div key={t} className="flex gap-3">
                  <div className="h-7 w-7 shrink-0 rounded-lg bg-primary/10 text-primary grid place-items-center text-sm font-heading font-bold">{i + 1}</div>
                  <div><p className="text-sm font-medium">{t}</p><p className="text-xs text-muted-foreground">{d}</p></div>
                </div>
              ))}
            </div>

            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">Example payload</p>
              <CodeBlock testid="dev-sms-curl">{smsCurl}</CodeBlock>
            </div>
          </Card>

          <Card className="p-5 space-y-3">
            <div className="flex items-center gap-2">
              <FlaskConical className="h-4 w-4 text-primary" />
              <h3 className="font-heading font-semibold">Webhook tester</h3>
            </div>
            <p className="text-sm text-muted-foreground">Paste a real bank credit SMS to verify parsing + matching against your pending orders.</p>
            {presets.length > 0 && (
              <div className="flex flex-wrap items-center gap-2" data-testid="sms-presets">
                <span className="text-xs text-muted-foreground">Bank templates:</span>
                {presets.map((p) => (
                  <Button key={p.bank} type="button" size="sm" variant="outline" className="h-7 text-xs"
                    onClick={() => { setSmsText(p.sample); setTestResult(null); }}
                    data-testid={`preset-${p.bank.toLowerCase()}`}>
                    {p.bank}
                  </Button>
                ))}
              </div>
            )}
            <Textarea rows={3} value={smsText} onChange={(e) => setSmsText(e.target.value)} className="font-mono text-xs" data-testid="dev-sms-input" />
            <Button onClick={runTest} disabled={testing} data-testid="dev-sms-test">
              {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FlaskConical className="mr-1.5 h-4 w-4" /> Send test to webhook</>}
            </Button>
            {testResult && (
              <div className={`rounded-lg border p-3 text-sm flex items-start gap-2 ${testResult.ok ? "border-success/40 bg-success/5" : "border-border bg-muted/40"}`} data-testid="dev-test-result">
                {testResult.ok
                  ? <><CheckCircle2 className="h-4 w-4 text-success mt-0.5 shrink-0" /><div><p className="font-medium text-success">Matched & settled</p><p className="font-mono text-xs text-muted-foreground">order {testResult.data.order_id} · ₹{testResult.data.amount} · UTR {testResult.data.utr}</p></div></>
                  : <><XCircle className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" /><div><p className="font-medium">{testResult.error ? "Error" : "Parsed, no match"}</p><p className="font-mono text-xs text-muted-foreground">{testResult.error || `amount ₹${testResult.data?.amount} · no pending order with this exact amount`}</p></div></>}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
